// Supervisor tools — the parent agent's surface for managing
// sub-agents that survived past their spawn tool's return.
//
// Background: spawn_subagent / spawn_batch normally block until the
// child(ren) finish. When the user steers the parent mid-spawn, the
// spawn tool returns early with a handle id and the child(ren) keep
// running in a per-chat childHandleRegistry (see
// childHandleRegistry.js). These four tools operate on that registry
// by id, giving the LLM the ability to redirect, stop, or wait on
// live children without aborting the whole parent run.
//
// Recursive supervision: await_subagent itself races against parent
// steer, so a second user message arriving mid-await surfaces as a
// new turn and the parent loop drains it the same way as the first.
//
// All four tools degrade gracefully when invoked with no live
// children — they return a clear "no such handle" error rather than
// crashing the loop.

import { Type } from "@earendil-works/pi-ai";

function noSuchHandleError(childId) {
    return {
        content: [{ type: "text", text: `error: no live sub-agent with id ${JSON.stringify(childId)}. Use list_subagents to see active handles.` }],
        isError: true,
    };
}

function formatRunResult(result) {
    if (!result) return "(no result)";
    if (result.status === "done") {
        if (result.result == null) return "(child finished with no result)";
        return typeof result.result === "string" ? result.result : JSON.stringify(result.result);
    }
    if (result.status === "aborted") return "sub-agent stopped";
    if (result.status === "error") return `error: ${result.error?.message || "sub-agent failed"}`;
    return JSON.stringify(result);
}

// Race a handle's promise against a parent-steer event. Returns
// { kind: "done", result } if the child finishes first, or
// { kind: "interrupted" } if the user steers the parent again while
// we were awaiting. Mirrors raceCompletionVsSteer in subAgentTools.js
// — kept separate so this file has no cross-cutting imports.
function raceWithSteer(promise, subscribeToParentSteer) {
    if (!subscribeToParentSteer) {
        return promise.then((result) => ({ kind: "done", result }));
    }
    return new Promise((resolve) => {
        let resolved = false;
        let unsubscribe = null;
        const finish = (r) => {
            if (resolved) return;
            resolved = true;
            if (unsubscribe) {
                try { unsubscribe(); } catch { /* noop */ }
            }
            resolve(r);
        };
        unsubscribe = subscribeToParentSteer(() => {
            finish({ kind: "interrupted" });
        });
        promise.then(
            (result) => finish({ kind: "done", result }),
            (err) => finish({ kind: "done", result: { status: "error", error: err, result: null, messages: [] } }),
        );
    });
}

export function createSupervisorTools({ childRegistry, subscribeToParentSteer } = {}) {
    if (!childRegistry) {
        // Defensive: without a registry the tools are useless. Return
        // an empty array so piChat's tools list isn't broken.
        return [];
    }

    const listSubagents = {
        name: "list_subagents",
        label: "List live sub-agents",
        description:
            "List every sub-agent still running in the background as a result of an interrupted spawn_subagent or spawn_batch. " +
            "Each entry has { id, kind, status, taskSummary }. " +
            "Use this when you've forgotten a handle id, or to discover whether anything is still in flight before responding to the user.",
        parameters: Type.Object({}),
        async execute() {
            const entries = childRegistry.list();
            if (!entries.length) {
                return { content: [{ type: "text", text: "No live sub-agents." }], details: { handles: [] } };
            }
            const lines = entries.map((e) =>
                `  • ${e.id} (${e.kind}, ${e.status}) — ${e.taskSummary || "(no task summary)"}`
            );
            return {
                content: [{ type: "text", text: `Live sub-agents (${entries.length}):\n${lines.join("\n")}` }],
                details: { handles: entries },
            };
        },
    };

    const steerSubagent = {
        name: "steer_subagent",
        label: "Steer sub-agent",
        description:
            "Inject a user message into a still-running sub-agent's loop without aborting it. The child picks up the new message at its next turn boundary and adapts. " +
            "For a batch handle, broadcasts the same message to every live item in the batch. " +
            "Use when the user's new direction means a running child needs new framing — e.g. an extra constraint, a clarification, a redirect.",
        parameters: Type.Object({
            child_id: Type.String({
                description: "The handle id returned by an interrupted spawn (e.g. 'sub_toolu_…' or 'batch_toolu_…'), or one listed by list_subagents.",
            }),
            message: Type.String({
                description: "The new instruction or context to inject. Phrased as if a user typed it — the child receives it as a user-role message.",
            }),
        }),
        async execute(_id, args) {
            const handle = childRegistry.get(args.child_id);
            if (!handle) return noSuchHandleError(args.child_id);
            const message = { role: "user", content: String(args.message ?? ""), timestamp: Date.now() };
            try {
                if (typeof handle.steer === "function") {
                    handle.steer(message);
                } else if (handle.child && typeof handle.child.steer === "function") {
                    handle.child.steer(message);
                } else {
                    return {
                        content: [{ type: "text", text: `error: handle ${args.child_id} doesn't support steering (no child reference).` }],
                        isError: true,
                    };
                }
                return {
                    content: [{ type: "text", text:
                        `Steer queued for ${args.child_id}. The ${handle.kind === "batch" ? "items" : "child"} will pick it up at the next turn boundary.`
                    }],
                };
            } catch (err) {
                return {
                    content: [{ type: "text", text: `error steering ${args.child_id}: ${err?.message || err}` }],
                    isError: true,
                };
            }
        },
    };

    const abortSubagent = {
        name: "abort_subagent",
        label: "Abort sub-agent",
        description:
            "Stop a still-running sub-agent. Its work is discarded and the handle is removed from the registry. " +
            "For a batch handle, aborts every live item in the batch (items that haven't yet started are simply not run). " +
            "Use when the user's redirection means the child's work is no longer relevant — don't wait for it to finish.",
        parameters: Type.Object({
            child_id: Type.String({ description: "Handle id from an interrupted spawn or list_subagents." }),
        }),
        async execute(_id, args) {
            const handle = childRegistry.get(args.child_id);
            if (!handle) return noSuchHandleError(args.child_id);
            try {
                handle.abort?.();
                childRegistry.remove(args.child_id);
                return {
                    content: [{ type: "text", text: `Aborted ${args.child_id}.` }],
                };
            } catch (err) {
                return {
                    content: [{ type: "text", text: `error aborting ${args.child_id}: ${err?.message || err}` }],
                    isError: true,
                };
            }
        },
    };

    const awaitSubagent = {
        name: "await_subagent",
        label: "Await sub-agent completion",
        description:
            "Block until a still-running sub-agent finishes, then return its result and remove the handle. " +
            "For a batch handle, waits for every item in the batch to settle and returns the aggregated final state. " +
            "Important: if the user steers you again while this await is in flight, the await returns early with a 'still running' marker and the new user message arrives in your next turn — you can re-issue await_subagent on the same id later. " +
            "Use when you actually need the child's result before responding.",
        parameters: Type.Object({
            child_id: Type.String({ description: "Handle id from an interrupted spawn or list_subagents." }),
        }),
        async execute(_id, args, signal) {
            const handle = childRegistry.get(args.child_id);
            if (!handle) return noSuchHandleError(args.child_id);

            // Race the handle's promise against (a) a fresh parent
            // steer event and (b) the agent's abort signal.
            const abortPromise = new Promise((_, reject) => {
                if (!signal) return;
                if (signal.aborted) {
                    reject(new Error("aborted"));
                    return;
                }
                signal.addEventListener("abort", () => reject(new Error("aborted")), { once: true });
            });

            let outcome;
            try {
                outcome = await Promise.race([
                    raceWithSteer(handle.promise, subscribeToParentSteer),
                    abortPromise,
                ]);
            } catch (err) {
                return {
                    content: [{ type: "text", text: `await of ${args.child_id} aborted: ${err?.message || err}` }],
                    isError: true,
                };
            }

            if (outcome.kind === "interrupted") {
                // Don't remove from registry — child is still alive.
                return {
                    content: [{ type: "text", text:
                        `await of ${args.child_id} interrupted by another user steer. The child is still running in the background; see the user's new message and decide whether to re-issue await_subagent, steer_subagent, or abort_subagent on this handle.`
                    }],
                    details: { handle_id: args.child_id, status: "running" },
                };
            }

            // Child finished. Drop the handle and surface the result.
            childRegistry.remove(args.child_id);
            const result = outcome.result;

            if (handle.kind === "batch") {
                // For a batch, the handle.promise resolves with the
                // batch's live state object (post-settle). Format a
                // brief summary matching spawn_batch's normal output.
                const finalState = result;
                if (finalState && typeof finalState === "object" && Array.isArray(finalState.items)) {
                    const done = finalState.done || 0;
                    const errored = finalState.errored || 0;
                    const stoppedCount = finalState.items.filter((i) => i.status === "stopped").length;
                    const total = finalState.total || finalState.items.length;
                    const text = [
                        `${done}/${total} succeeded`,
                        errored ? `${errored} errored` : null,
                        stoppedCount ? `${stoppedCount} stopped` : null,
                    ].filter(Boolean).join(", ");
                    return {
                        content: [{ type: "text", text: `Batch ${args.child_id} finished: ${text}` }],
                        details: { handle_id: args.child_id, ...finalState },
                    };
                }
                return {
                    content: [{ type: "text", text: `Batch ${args.child_id} finished.` }],
                    details: { handle_id: args.child_id, result: finalState },
                };
            }

            // Single sub-agent: result is { status, result, resultRef, messages, error }.
            return {
                content: [{ type: "text", text: formatRunResult(result) }],
                details: { handle_id: args.child_id, ...result },
                isError: result?.status === "error",
            };
        },
    };

    return [listSubagents, steerSubagent, abortSubagent, awaitSubagent];
}
